import { type CSSProperties, type ReactNode, useMemo } from "react";
import {
  AdditionalDataKeys,
  type LinearSubstep,
  type SubstepTile,
  type SubstepTileExtended,
} from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import LinearTile from "~/features/configurator/Linear/components/LinearTile";
import { defaultMemoize } from "reselect";
import { type CustomDispatch } from "~/app/store/types";
import { updateCsticValuesFromTile } from "~/features/configurator/Linear/redux/actions";
import { getKmats } from "~/features/configurator/Linear/redux/selectors";
import { formatCsticsToSet } from "~/features/configurator/Linear/utils";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { useTranslation } from "react-i18next";
import { SubstepFooterInfoType } from "~/features/configurator/Linear/dtos";

import classNames from "classnames";
import { styled } from "~/common/mui";
import { isTileEnabled, isTileStandard } from "~/features/configurator/common/tileUtils";
import { Alert } from "~/common/mui/Alert/Alert";
import { Grid, Stack } from "~/common/mui";

const TileContainerRoot = styled("div")(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "repeat(var(--column-amount, 12), minmax(0, 1fr))",
  gap: "var(--gutter)",
  marginBottom: theme.spacing(4),
}));

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

export const makeRenderTile = (debugLanguage: boolean, def: LinearSubstep) => (data: SubstepTile) => {
  let title: ReactNode = debugLanguage ? (
    formatCsticsToSet(data.cstics)
  ) : (
    <LanguageAwareExternalContent content={data.name} className={"scha__lc__external-label"} />
  );
  const customFontSize = def.additionalData[AdditionalDataKeys.TitleFontSize] as string | number | undefined;
  if (customFontSize) {
    title = <span style={{ fontSize: customFontSize }}>{title}</span>;
  }
  return title;
};

export const GenericTileSubstep = ({ def, ...props }: Props) => {
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const tileData = getTileData(def, dispatch);
  const kmats = useSelector(getKmats);

  const { t } = useTranslation();
  const debugLanguage = isDebugLanguageEnabled();
  const renderTitle = makeRenderTile(debugLanguage, def);

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={def.selectedTile !== null}
      showError={def.isInvalid}
      errorMessage={def.isInvalid ? t("linear.step.select.option") : ""}
      {...props}
    >
      <Stack gap={"var(--gutter)"} sx={{ width: "100%", mb: 4 }}>
        {tileData.map((row, rowIndex) => (
          <Grid key={rowIndex} columns={def.tilesPerRow} container gap={"var(--gutter)"}>
            {row.map((item, itemIndex) => (
              <Grid key={itemIndex} size={1}>
                <LinearTile
                  onClickHandler={item.onClick}
                  imageUrl={item.imageUrl}
                  title={renderTitle(item)}
                  description={item.description}
                  info={item.info}
                  isPicked={item.tileIndex === def.selectedTile}
                  isDisabled={!isTileEnabled(item, kmats)}
                  isStandard={isTileStandard(item, kmats)}
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </Stack>
      {def.footerInfo.length > 0 && (
        <div className="mb-3 d-flex flex-direction-column">{def.footerInfo.map(makeRenderFooter(def))}</div>
      )}
    </SubstepFrame>
  );
};

const makeRenderFooter = (def: LinearSubstep) => {
  const footerType = def.additionalData[AdditionalDataKeys.FooterType] ?? SubstepFooterInfoType.Message;
  if (footerType === SubstepFooterInfoType.Message) {
    return (info: string) => (
      <Alert key={info} className="mb-2" severity="info">
        <LanguageAwareExternalContent content={info} className={"scha__lc__external-label"} />
      </Alert>
    );
  }
  return (info: string) => <LanguageAwareExternalContent content={info} className={"mb-2 scha__lc__external-label"} />;
};

export const getTileData = defaultMemoize(
  (stepElement: LinearSubstep, dispatch: CustomDispatch<RootState>): SubstepTileExtended[][] => {
    if (stepElement.groupedTiles.length > 0) {
      let index = 0;
      return stepElement.groupedTiles.map((groupedTile) => {
        return groupedTile.tiles.map((tile) => ({
          ...tile,
          onClick: () => dispatch(updateCsticValuesFromTile(stepElement.type, index, tile)),
          tileIndex: index++,
        }));
      });
    }
    const allTiles: SubstepTileExtended[] = stepElement.tiles.map((tile, index) => ({
      ...tile,
      onClick: () => dispatch(updateCsticValuesFromTile(stepElement.type, index, tile)),
      tileIndex: index,
    }));

    return [allTiles];
  },
);

export type TileContainerProps = JSX.IntrinsicElements["div"] & {
  columnAmount?: number;
};

export const TileContainer = ({ style: externalStyle, columnAmount, className, ...rest }: TileContainerProps) => {
  const style = useMemo(() => {
    let style = undefined;
    if (externalStyle) {
      style = { ...externalStyle };
    }
    if (columnAmount) {
      style = { ...style, "--column-amount": columnAmount } as CSSProperties;
    }
    return style;
  }, [externalStyle, columnAmount]);

  return <TileContainerRoot style={style} className={className} {...rest} />;
};
